// ─── Canonical Finishing Method Runtime Resolver ───
// Maps UI finishing identity (sourceType + sourceCode) → canonical Backend UUID.
// NO UUID hardcoding, NO display-name matching. Identity is only sourceType + sourceCode.

import { getFinishingQuoteMethods } from '@/lib/quotationService';

export type FinishingSourceType = 'finish_l1' | 'print';

export interface CanonicalFinishingMethod {
  id: string;
  code: string;
  name: string;
  sourceType: FinishingSourceType;
  sourceCode: string;
}

/** Compound identity key, e.g. "finish_l1:01" or "print:03". */
export function finishingLookupKey(sourceType: string, sourceCode: string): string {
  return `${sourceType}:${sourceCode}`;
}

// Module-level cache so methods are loaded once per page/query lifecycle.
let cachedLookup: Map<string, CanonicalFinishingMethod> | null = null;
let loadPromise: Promise<Map<string, CanonicalFinishingMethod>> | null = null;

export function clearFinishingLookupCache(): void {
  cachedLookup = null;
  loadPromise = null;
}

/** Load the canonical method master once and build a sourceType:sourceCode → method lookup. */
export async function loadFinishingMethodLookup(): Promise<Map<string, CanonicalFinishingMethod>> {
  if (cachedLookup) return cachedLookup;
  if (!loadPromise) {
    loadPromise = getFinishingQuoteMethods()
      .then((data) => {
        const map = new Map<string, CanonicalFinishingMethod>();
        for (const m of data.methods) {
          map.set(finishingLookupKey(m.sourceType, m.sourceCode), {
            id: m.id,
            code: m.code,
            name: m.name,
            sourceType: m.sourceType,
            sourceCode: m.sourceCode,
          });
        }
        cachedLookup = map;
        return map;
      })
      .catch((err) => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
}

/**
 * Resolve a UI selection to its canonical Backend method.
 * Returns null when identity is missing or no Backend method matches.
 * NEVER falls back to name matching or a fabricated UUID.
 */
export function resolveFinishingMethod(
  lookup: Map<string, CanonicalFinishingMethod>,
  sourceType: string | undefined,
  sourceCode: string | undefined,
): CanonicalFinishingMethod | null {
  if (!sourceType || !sourceCode) return null;
  return lookup.get(finishingLookupKey(sourceType, sourceCode)) ?? null;
}